import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, AlertTriangle, Megaphone, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Frozen at build time by the Dockerfile (`RUN export REACT_APP_BUILD_ID=…`).
// In dev / preview the env var is undefined, which is fine — we treat it as
// "unknown" and just skip the up-to-date badge instead of misleading anyone.
const BUNDLED_BUILD_ID = process.env.REACT_APP_BUILD_ID || null;

/**
 * Performs a true "force full refresh":
 *  - Unregisters every service worker
 *  - Deletes every CacheStorage entry
 *  - Wipes sessionStorage entirely
 *  - Wipes localStorage EXCEPT the auth-critical keys so the user stays signed in
 *  - Triggers a cache-busting reload
 */
/**
 * Cleanup-only variant: same purge as performFullRefresh but DOESN'T reload.
 * Used when we want a fresh-cache state but the page is about to take over
 * (e.g. after magic-link exchange — we already strip ?t= and want a single
 * cache-busted reload, not two).
 */
export async function performCacheCleanup({ preserveAuth = true } = {}) {
  try {
    const preserve = preserveAuth
      ? ["token", "username", "role", "permissions", "chapter", "title"]
      : [];
    const saved = {};
    preserve.forEach((k) => {
      const v = localStorage.getItem(k);
      if (v !== null) saved[k] = v;
    });

    try { localStorage.clear(); } catch (e) { /* ignore */ }
    try { sessionStorage.clear(); } catch (e) { /* ignore */ }

    Object.entries(saved).forEach(([k, v]) => {
      try { localStorage.setItem(k, v); } catch (e) { /* ignore */ }
    });

    if ("serviceWorker" in navigator) {
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      } catch (e) { /* ignore */ }
    }

    if ("caches" in window) {
      try {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      } catch (e) { /* ignore */ }
    }
  } catch (e) {
    console.warn("Cache cleanup encountered an issue:", e);
  }
}

export async function performFullRefresh() {
  await performCacheCleanup({ preserveAuth: true });

  // Persist the latest known server version so the banner doesn't re-trigger
  try {
    const v = await axios.get(`${API}/app-version`, { timeout: 4000 });
    if (v?.data?.current_version) {
      localStorage.setItem("app_version", String(v.data.current_version));
    }
  } catch (e) { /* ignore */ }

  // Cache-busting reload (the query string defeats CDN/browser cached HTML)
  const url = new URL(window.location.href);
  url.searchParams.set("_r", Date.now().toString());

  // iOS Safari fix (June 2026): on iPhone the back-forward cache (bfcache)
  // can serve a stale index.html even after caches.delete() if the response
  // headers don't say no-store. Two extra moves to force a fresh fetch:
  //   1. Pre-warm a no-store fetch of the new URL so Safari's HTTP cache
  //      gets the fresh HTML BEFORE the navigation, defeating bfcache.
  //   2. Use `location.href = ...` (a fresh navigation) instead of
  //      `location.replace(...)` (which iOS treats as a back-nav under
  //      some conditions and can still hit bfcache).
  try {
    await fetch(url.toString(), { cache: "no-store", credentials: "same-origin" });
  } catch (e) { /* network failure is non-fatal; the navigation below still happens */ }

  // Detect iOS to pick the most aggressive navigation API. On non-iOS,
  // location.replace is preferable (no extra history entry).
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (isIOS) {
    window.location.href = url.toString();
  } else {
    window.location.replace(url.toString());
  }
}

/**
 * Topbar button. Click → confirm dialog → performs full refresh.
 * National admins additionally see a "Force refresh for all users" option.
 */
export default function ForceRefreshButton({ userRole, userChapter, compact = false }) {
  const [open, setOpen] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [deployInfo, setDeployInfo] = useState(null);
  // null = checking | "match" = current | "stale" = update available | "unknown" = no build_id baked in (dev)
  const [buildStatus, setBuildStatus] = useState(null);
  const [liveBuild, setLiveBuild] = useState(null);

  // Pull the deployed commit SHA + build time when the dialog opens so the
  // user can immediately tell if a push has landed in production.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    axios
      .get(`${API}/deploy-info`, { timeout: 4000 })
      .then((res) => { if (!cancelled) setDeployInfo(res.data); })
      .catch(() => { if (!cancelled) setDeployInfo({ commit_sha: "unreachable", build_time: null }); });

    // Compare the build_id baked into THIS JS bundle vs the live
    // build-info.json on the server. If they differ, this user's JS is
    // stale and they need to refresh. (June 2026)
    if (!BUNDLED_BUILD_ID) {
      setBuildStatus("unknown");
    } else {
      fetch(`/build-info.json?_=${Date.now()}`, { cache: "no-store" })
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((data) => {
          if (cancelled) return;
          setLiveBuild(data);
          setBuildStatus(
            data?.build_id && String(data.build_id) === String(BUNDLED_BUILD_ID)
              ? "match"
              : "stale"
          );
        })
        .catch(() => {
          if (!cancelled) setBuildStatus("unknown");
        });
    }

    return () => { cancelled = true; };
  }, [open]);

  const isNationalAdmin = userRole === "admin" && userChapter === "National";

  const handleSelf = async () => {
    setBusy(true);
    toast.info("Refreshing… clearing cache and reloading.");
    await performFullRefresh();
  };

  const handleBroadcast = async () => {
    try {
      setBusy(true);
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/app-version/bump`,
        { message: message.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("All connected users will be prompted to refresh within 60 seconds.");
      setBroadcastOpen(false);
      setMessage("");
      // Refresh self too, so the admin's own client picks up new assets
      await performFullRefresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to broadcast refresh.";
      toast.error(msg);
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="outline"
        size="sm"
        data-testid="force-refresh-button"
        title="Force full refresh (clear cache)"
        className="flex items-center gap-2 text-xs sm:text-sm bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600"
      >
        <RefreshCw className="w-4 h-4" />
        {!compact && <span className="hidden sm:inline">Refresh</span>}
      </Button>

      {/* Self-refresh confirm */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-blue-400" />
              Force Full Refresh
            </DialogTitle>
            <DialogDescription className="text-slate-300">
              This clears the browser&apos;s cached assets, service workers and local data so you get the
              very latest version of bohhub.com. You will stay signed in.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-slate-400 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-400 flex-shrink-0" />
            <span>Unsaved form data on this page will be lost.</span>
          </div>

          {/* Version status badge — tells the user whether they're already
              running the latest deployed JS bundle, or if a newer one is
              live and they need to refresh. */}
          {buildStatus === "match" && (
            <div
              data-testid="build-status-up-to-date"
              className="flex items-center gap-2 text-sm rounded-md p-3 border bg-emerald-900/20 border-emerald-700 text-emerald-200"
            >
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              <span>
                You&apos;re on the latest version
                {liveBuild?.build_time ? ` (built ${new Date(liveBuild.build_time).toLocaleString()})` : ""}.
              </span>
            </div>
          )}
          {buildStatus === "stale" && (
            <div
              data-testid="build-status-stale"
              className="flex items-center gap-2 text-sm rounded-md p-3 border bg-amber-900/20 border-amber-600 text-amber-100"
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>
                A newer version is available
                {liveBuild?.build_time ? ` (built ${new Date(liveBuild.build_time).toLocaleString()})` : ""}.
                Tap <strong>Refresh Now</strong> to load it.
              </span>
            </div>
          )}
          {buildStatus === null && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <RefreshCw className="w-3 h-3 animate-spin" />
              <span>Checking for new version…</span>
            </div>
          )}

          {deployInfo && (
            <div
              className="bg-slate-900/60 border border-slate-700 rounded-md p-3 text-xs text-slate-300 space-y-1"
              data-testid="deploy-info-block"
            >
              <div className="text-slate-400 uppercase tracking-wide text-[10px] font-semibold">
                Deployed Build
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-slate-400">Commit:</span>
                <code className="text-emerald-400" data-testid="deploy-info-sha">
                  {deployInfo.commit_sha || "unknown"}
                </code>
              </div>
              {deployInfo.build_time && (
                <div className="flex justify-between gap-2">
                  <span className="text-slate-400">Built:</span>
                  <span className="text-slate-200">
                    {new Date(deployInfo.build_time).toLocaleString()}
                  </span>
                </div>
              )}
              <div className="text-slate-500 text-[10px] pt-1">
                Compare the commit hash with your latest GitHub commit — if they match, the deploy succeeded and any stale UI is just browser cache (this refresh will clear it).
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            {isNationalAdmin && (
              <Button
                variant="outline"
                disabled={busy}
                data-testid="broadcast-refresh-open-button"
                onClick={() => {
                  setOpen(false);
                  setBroadcastOpen(true);
                }}
                className="bg-amber-700 text-white border-amber-600 hover:bg-amber-600"
              >
                <Megaphone className="w-4 h-4 mr-1" />
                Force refresh for all users
              </Button>
            )}
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => setOpen(false)}
              className="bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600"
              data-testid="force-refresh-cancel-button"
            >
              Cancel
            </Button>
            <Button
              disabled={busy}
              onClick={handleSelf}
              data-testid="force-refresh-confirm-button"
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {busy ? "Refreshing…" : "Refresh Now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Broadcast dialog (National admin only) */}
      {isNationalAdmin && (
        <Dialog open={broadcastOpen} onOpenChange={setBroadcastOpen}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-amber-400" />
                Force Refresh for All Users
              </DialogTitle>
              <DialogDescription className="text-slate-300">
                Every signed-in user will see a banner prompting them to refresh within 60 seconds.
                Use this after a deploy that includes breaking changes.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="broadcast-message" className="text-slate-200">
                Optional message (shown in the banner)
              </Label>
              <Input
                id="broadcast-message"
                data-testid="broadcast-refresh-message-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="e.g. New Treasury features deployed"
                maxLength={140}
                className="bg-slate-700 text-white border-slate-600"
              />
            </div>
            <DialogFooter className="gap-2 flex-col sm:flex-row">
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => setBroadcastOpen(false)}
                className="bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600"
                data-testid="broadcast-refresh-cancel-button"
              >
                Cancel
              </Button>
              <Button
                disabled={busy}
                onClick={handleBroadcast}
                data-testid="broadcast-refresh-confirm-button"
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {busy ? "Sending…" : "Broadcast & Refresh Me"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
